import {
  createTwinSenseMessage,
  getEdgeDevices,
  getFrameSplitConfig,
  getRTSPEndpoints,
  type FrameSplitObserver,
} from '@repo/front-end-services';
import {
  TwinSenseMessageDirection,
  TwinSenseMessageKind,
  TwinSenseMessageType,
  TwinSenseQosLevel,
  type CreateTwinSenseMessagePayload,
  type RTSPEndpoint,
} from '@repo/frontend-types';

/** Minimal edge-device shape returned by `GET api/twins/:bid/devices`. */
interface EdgeDevice {
  id: number;
  bid?: string;
  software_version?: string;
}

export interface SendFrameSplitConfigParams {
  middlewareApi: string;
  accessToken: string;
  /** Twin BID — resolves the twin's edge devices. */
  twinBid: string;
  /** Twin ID — resolves the RTSP endpoints used to scope observers per device. */
  twinId: number;
  /** The active TwinSense config whose observers we push (also the correlation id). */
  configId: number;
  /** Epoch-ms stamped into the message header. Injectable for deterministic tests. */
  now?: number;
}

export interface SendFrameSplitConfigResult {
  /** Edge devices resolved for the twin. */
  devices: number;
  /** Devices a frame-split-config message was published to successfully. */
  sent: number;
  /**
   * Devices the config did not successfully reach: either the message send errored (transport or
   * server error), or — on a multi-device twin — observers could not be scoped because the RTSP
   * endpoint read did not resolve, so no empty config was pushed.
   */
  failed: number;
}

/**
 * Publishes the just-activated observers to the twin's edge device(s) as a
 * `FrameSplitConfig` MQTT message — the same envelope the admin "Send Message"
 * screen builds by hand, generated automatically at Go Live so the device
 * starts measuring the drawn observers without a manual admin send.
 *
 * Mirrors the admin send flow: `getFrameSplitConfig` returns each observer's
 * server-derived `frame_config` (bounding box), which is exactly the shape the
 * middleware FlatBuffer encoder expects. Observers are scoped per device by
 * `rtsp_bid` so a device is only told about frames from its own cameras — with a
 * single-device fallback (below) that sends every observer when scoping can't
 * map any, which is the common one-device twin.
 *
 * Best-effort by contract: the graph and observer records are already live by
 * the time this runs, and server-side MQTT publish is itself best-effort (it is
 * skipped when the broker is disconnected). This never throws; callers read the
 * counts and, at most, log. Header uses `TwinSenseMessageKind.FRAME_SPLIT_CONFIG`
 * (`'FrameSplitConfig'`) — the literal the encoder dispatches on; any other value
 * would encode an empty body.
 */
export const sendFrameSplitConfig = async ({
  middlewareApi,
  accessToken,
  twinBid,
  twinId,
  configId,
  now = Date.now(),
}: SendFrameSplitConfigParams): Promise<SendFrameSplitConfigResult> => {
  const [devicesRaw, frameSplitConfig, endpoints] = await Promise.all([
    getEdgeDevices(middlewareApi, accessToken, twinBid) as Promise<EdgeDevice[] | undefined>,
    getFrameSplitConfig(middlewareApi, accessToken, configId),
    getRTSPEndpoints(middlewareApi, accessToken, twinId),
  ]);

  // Only devices with a BID can be addressed (the BID is the MQTT topic root).
  const devices = (devicesRaw ?? []).filter(
    (d): d is EdgeDevice & { bid: string } => typeof d?.bid === 'string' && d.bid.length > 0,
  );

  // Nothing to send if there is no device to send to or no config to send.
  if (devices.length === 0 || !frameSplitConfig) {
    return { devices: devices.length, sent: 0, failed: 0 };
  }

  const singleDevice = devices.length === 1;
  const allObservers = frameSplitConfig.observers ?? [];
  // `getRTSPEndpoints` returns [] on failure, so an empty list on a MULTI-device twin means scoping
  // is impossible: every device would be sent an empty observer set. The single-device fallback
  // below covers the one-device case; multi-device has no such fallback, so guard it explicitly
  // rather than silently publishing empty configs and reporting them as clean sends.
  const endpointsResolved = Array.isArray(endpoints) && endpoints.length > 0;

  let sent = 0;
  let failed = 0;

  for (const device of devices) {
    // The RTSP endpoint BIDs this device streams. `getRTSPEndpoints` returns []
    // on failure, so an empty set is indistinguishable from "device has no
    // mapped endpoints" — the single-device fallback below covers both.
    const deviceRtspBids = new Set(
      (endpoints ?? [])
        .filter((e: RTSPEndpoint) => e.twin_device_id === device.id)
        .map((e: RTSPEndpoint) => e.bid)
        .filter((bid): bid is string => !!bid),
    );

    let observers: FrameSplitObserver[] = allObservers.filter(
      o => o.rtsp_bid != null && deviceRtspBids.has(o.rtsp_bid),
    );

    // Single-device twin: if scoping mapped nothing (no endpoints resolved, or
    // the endpoint→device links aren't set), send every observer — they can only
    // belong to this one device anyway.
    if (observers.length === 0 && singleDevice) {
      observers = allObservers;
    }

    // Multi-device twin whose RTSP endpoint read yielded nothing usable: we cannot scope observers
    // to this device. Publishing an empty config here and counting it as `sent` is the silent
    // failure — the drawn observers never reach any camera. Skip the send and count it failed so the
    // caller surfaces it (the graph is already live; a later retry/manual send can push the config).
    if (observers.length === 0 && !singleDevice && allObservers.length > 0 && !endpointsResolved) {
      console.warn(
        `[sendFrameSplitConfig] Could not scope observers to device ${device.bid}: RTSP endpoints ` +
          `did not resolve (twinId=${twinId}, configId=${configId}). Skipping to avoid pushing an ` +
          `empty frame-split config.`,
      );
      failed += 1;
      continue;
    }

    const message = {
      header: {
        schema_version: 1,
        message_type: TwinSenseMessageType.CONFIG,
        message_kind: TwinSenseMessageKind.FRAME_SPLIT_CONFIG,
        message_version: 1,
        edge_device_bid: device.bid,
        software_version: device.software_version || '',
        model_version: '',
        event_time_ms: now,
        send_time_ms: now,
        sequence_nr: 0,
        qos: TwinSenseQosLevel.QOS_DEFAULT,
        flags: 0,
        correlation_id: String(configId),
      },
      body: {
        configuration_id: configId,
        observers,
      },
    };

    const payload: CreateTwinSenseMessagePayload = {
      device_id: device.id,
      topic: `${device.bid}/${TwinSenseMessageType.CONFIG}/${TwinSenseMessageKind.FRAME_SPLIT_CONFIG}`,
      message_content: JSON.stringify(message),
      message_type: TwinSenseMessageType.CONFIG,
      message_kind: TwinSenseMessageKind.FRAME_SPLIT_CONFIG,
      message_direction: TwinSenseMessageDirection.SENT,
      schema_version: 1,
      message_version: 1,
      flags: 0,
      correlation_id: String(configId),
      qos_level: TwinSenseQosLevel.QOS_DEFAULT,
    };

    try {
      const { error } = await createTwinSenseMessage(middlewareApi, accessToken, payload);
      if (error) {
        failed += 1;
      } else {
        sent += 1;
      }
    } catch {
      failed += 1;
    }
  }

  return { devices: devices.length, sent, failed };
};
